import React, { useEffect, useState } from 'react';
import css from '@emotion/css';
import styled from '@emotion/styled';

export interface IChordLine {
  progress?: number;
  threshold?: number;
  speedInSeconds?: number;
  selected?: boolean;
  isEndOfLine?: boolean;
  isEndOfNote?: boolean;
  isTransitionDisabled?: boolean;
  hideProgress?: boolean;
}

const ChordLine: React.FC<IChordLine> = ({
  progress = 0,
  threshold = 0,
  speedInSeconds = 0,
  isEndOfLine = false,
  isEndOfNote = false,
  isTransitionDisabled = false,
  hideProgress = false,
}) => {
  const [isNoteEndSelected, setIsNoteEndSelected] = useState<boolean>(false);

  const lineProgress =
    progress >= threshold + 100
      ? 100
      : progress >= threshold + 70
      ? 75
      : progress >= threshold + 50
      ? 50
      : progress >= threshold + 25
      ? 25
      : 0;

  // const calculateLineProgress = () => {
  //   if(progress >= threshold + 100) return 100;
  //   if(progress < threshold ) return 0;
  //   return (progress - threshold)
  // }
  // const lineProgress = calculateLineProgress();
  
  useEffect(() => {
    if (lineProgress === 100) {
      const interval = setInterval(
        () => setIsNoteEndSelected(true),
        speedInSeconds * 1000,
      );
      return () => clearInterval(interval);
    }
    if (lineProgress === 0) {
      setIsNoteEndSelected(false);
    }
    return () => {};
  }, [lineProgress, speedInSeconds]);

  return (
    <ChordLineContainer>
      <ChordLineProgress
        progress={lineProgress}
        speedInSeconds={speedInSeconds}
        hideProgress={hideProgress}
        isTransitionDisabled={isTransitionDisabled}
      />
      <ChordLineLeftEnd selected={!hideProgress && lineProgress > 0} />
      {isEndOfLine && (
        <ChordLineRightEnd selected={!hideProgress && isNoteEndSelected} />
      )}
      {isEndOfNote && (
        <ChordLineNoteEnd selected={!hideProgress && isNoteEndSelected} />
      )}
    </ChordLineContainer>
  );
};

export default ChordLine;

const ChordLineContainer = styled.div<IChordLine>`
  background-color: #1e1e1e;
  height: 4px;
  min-height: 4px;
  max-height: 4px;
  width: 100%;
  position: relative;

  html[color-mode='dark'] & {
    background-color: #ffffff;
  }

  .print & {
    background-color: #1e1e1e !important;
  }

  @media screen and (max-width: 1300px) {
    height: 3px;
  }

  @media screen and (max-width: 500px) {
    height: 2px;
    min-height: 2px;
    max-height: 2px;
  }

  ${({ selected }) =>
    selected &&
    css`
      &&& {
        background-color: #0a71c6;
      }
    `};
`;

const ChordLineProgress = styled.div<IChordLine>`
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: #0a71c6;

  width: 100%;

  transform-origin: 0% 100%;
  transform: scaleX(0);
  ${({ hideProgress }) =>
    hideProgress &&
    css`
      display: none;
    `}

  ${({ progress = 0 }) =>
    progress &&
    css`
      // width: ${progress}%;
      transform: scaleX(${progress/100});
    `};

  ${({ speedInSeconds, isTransitionDisabled = false }) =>
    speedInSeconds && isTransitionDisabled
      ? css`
          transition: none;
        `
      : css`
          transition: transform ${speedInSeconds}s linear;
        `};
`;

const ChordLineEnd = styled.div<IChordLine>`
  width: 4px;
  height: 16px;
  background-color: #1e1e1e;
  position: absolute;
  top: -5.33px;

  html[color-mode='dark'] & {
    background-color: #ffffff;
  }

  .print & {
    background-color: #1e1e1e !important;
  }

  ${({ selected }) =>
    selected &&
    css`
      &&& {
        background-color: #0a71c6;
      }
    `};

  @media screen and (max-width: 1300px) {
    height: 13px;
    width: 5px;
    top: -4.33px;
  }

  @media screen and (max-width: 500px) {
    height: 8px;
    width: 2px;
    max-width: 2px;
    min-width: 2px;
    top: -2.33px;
  }
`;

const ChordLineLeftEnd = styled(ChordLineEnd)`
  left: 0;
`;

const ChordLineRightEnd = styled(ChordLineEnd)`
  right: 0;

  .chord-line:last-child & {
    display: block;
  }
`;

const ChordLineNoteEnd = styled(ChordLineRightEnd)`
  right: -5px;

  @media screen and (max-width: 1300px) {
    right: -4.5px;
  }
`;
